import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { ExportMenu } from "@/components/ExportMenu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CaretLeft, Buildings, Envelope, Phone, MapPin, Coins, Receipt, FileText, ClipboardText, DownloadSimple, CheckCircle, Printer } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";

const fmt = (n) => `₱${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function KpiTile({ label, value, tone = "default", icon: Icon, testId }) {
  const toneCls = tone === "dark" ? "bg-foreground text-background" : "bg-card";
  return (
    <div className={`border border-border p-5 ${toneCls}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <span className={`text-[10px] uppercase tracking-widest ${tone === "dark" ? "text-background/60" : "text-muted-foreground"} font-medium`}>{label}</span>
        {Icon && <Icon size={16} className={tone === "dark" ? "text-background/70" : "text-muted-foreground"} />}
      </div>
      <div className="font-heading font-black tabular-nums text-2xl sm:text-3xl leading-none tracking-tight">{value}</div>
    </div>
  );
}

export default function ClientDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const seesReports = can(user, ["admin", "finance"]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  // Filter for the Invoices tab. Defaults to "paid" so the client record
  // opens straight to the reporting-friendly view of settled invoices.
  const [invoiceFilter, setInvoiceFilter] = useState("paid");

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, r] = await Promise.all([
          api.get(`/clients/${id}/related`),
          seesReports ? api.get(`/client-reports`, { params: { client_id: id } }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        setData(data);
        setReports(r.data || []);
      } catch (e) {
        toast.error("Client not found");
        nav("/clients");
      } finally { setLoading(false); }
    })();
  }, [id, nav, seesReports]);

  if (loading || !data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const c = data.client;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6" data-testid="client-detail-page">
      <div className="flex items-center gap-3">
        <Link to="/clients" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="back-to-clients">
          <CaretLeft size={12} /> Clients
        </Link>
      </div>

      {/* Header */}
      <div className="border-b border-border pb-6 flex items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="section-caption mb-2 flex items-center gap-2"><Buildings size={12} /> Client Profile</div>
          <h1 className="section-title text-3xl sm:text-4xl truncate">{c.name}</h1>
          <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
            {c.contact_person && <span>{c.contact_person}</span>}
            {c.email && <span className="inline-flex items-center gap-1"><Envelope size={12} /> {c.email}</span>}
            {c.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {c.phone}</span>}
            {c.address && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {c.address}</span>}
          </div>
        </div>
        <ExportMenu endpointPath={`/exports/client/${c.id}`} baseName={`client_${c.name.replace(/\s+/g,'_')}`} testId="export-client-detail" />
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiTile label="Contract Value" value={c.contract_value ? fmt(c.contract_value) : "—"} icon={Coins} tone="dark" testId="kpi-contract-value" />
        <KpiTile label="Invoiced" value={fmt(data.totals.invoiced || 0)} icon={FileText} testId="kpi-invoiced" />
        <KpiTile label="Collected" value={fmt(data.totals.collected || 0)} icon={CheckCircle} testId="kpi-collected" />
        <KpiTile label="Outstanding" value={fmt(data.totals.outstanding || 0)} icon={Receipt} testId="kpi-outstanding" />
      </div>

      {/* Contract details */}
      <div className="border border-border rounded-md bg-card p-6">
        <div className="section-caption mb-2">Contract</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Type</div>
            <div className="text-sm">{c.contract_type || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Value</div>
            <div className="text-sm tabular-nums">{c.contract_value ? fmt(c.contract_value) : "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Tax ID</div>
            <div className="text-sm font-mono">{c.tax_id || "—"}</div>
          </div>
          <div className="md:col-span-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Notes</div>
            <div className="text-sm">{c.notes || "—"}</div>
          </div>
        </div>
      </div>

      {/* Linked records */}
      <Tabs defaultValue="invoices">
        <TabsList className="border border-border">
          <TabsTrigger value="invoices" data-testid="tab-linked-invoices">Invoices ({data.counts.invoices || 0})</TabsTrigger>
          <TabsTrigger value="quotations" data-testid="tab-linked-quotations">Quotations ({data.counts.quotations})</TabsTrigger>
          <TabsTrigger value="pos" data-testid="tab-linked-pos">Purchase Orders ({data.counts.purchase_orders})</TabsTrigger>
          <TabsTrigger value="billings" data-testid="tab-linked-billings">Billing ({data.counts.billings})</TabsTrigger>
          {seesReports && (
            <TabsTrigger value="reports" data-testid="tab-linked-reports">Reports ({reports.length})</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          {(() => {
            const invoices = data.invoices || [];
            const filtered = invoiceFilter === "all"
              ? invoices
              : invoices.filter((i) => (i.payment_status || "").toLowerCase() === invoiceFilter);
            const totalEwt = filtered.reduce((s, i) => s + (i.payments || []).reduce((ps, p) => ps + (Number(p.ewt_amount) || 0), 0), 0);
            // Aging buckets — computed from every OUTSTANDING invoice (balance > 0)
            // regardless of the tab filter, since Finance needs the true AR aging
            // even when they toggled the tab to "Paid only".
            const today = new Date();
            const buckets = { "0-30": 0, "31-60": 0, "61-90": 0, "90+": 0 };
            for (const inv of invoices) {
              const bal = Number(inv.balance) || 0;
              if (bal <= 0) continue;
              const dueStr = inv.due_date || inv.issue_date || (inv.created_at || "").slice(0, 10);
              if (!dueStr) { buckets["0-30"] += bal; continue; }
              const days = Math.floor((today - new Date(dueStr)) / (1000 * 60 * 60 * 24));
              if (days <= 30) buckets["0-30"] += bal;
              else if (days <= 60) buckets["31-60"] += bal;
              else if (days <= 90) buckets["61-90"] += bal;
              else buckets["90+"] += bal;
            }
            const bucketTones = {
              "0-30": "border-emerald-200 bg-emerald-50/60 text-emerald-800",
              "31-60": "border-amber-200 bg-amber-50/60 text-amber-800",
              "61-90": "border-orange-200 bg-orange-50/60 text-orange-800",
              "90+": "border-rose-300 bg-rose-50/70 text-rose-800",
            };
            const totalOutstanding = Object.values(buckets).reduce((a, b) => a + b, 0);
            return (
              <>
                {totalOutstanding > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="aging-buckets">
                    {Object.entries(buckets).map(([range, amt]) => (
                      <div key={range} className={`border-2 rounded-md p-3 ${bucketTones[range]}`} data-testid={`aging-bucket-${range}`}>
                        <div className="text-[10px] uppercase tracking-widest opacity-80">{range === "90+" ? "90+ days · CRITICAL" : `${range} days`}</div>
                        <div className="font-heading font-black text-lg tabular-nums leading-tight mt-0.5">{fmt(amt)}</div>
                        <div className="text-[10px] opacity-70">{totalOutstanding > 0 ? Math.round((amt / totalOutstanding) * 100) : 0}% of AR</div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="uppercase tracking-widest text-muted-foreground">Show</span>
                    {[
                      { v: "paid", label: "Paid only" },
                      { v: "partial", label: "Partial" },
                      { v: "pending", label: "Pending" },
                      { v: "all", label: "All" },
                    ].map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setInvoiceFilter(o.v)}
                        className={`px-2.5 py-1 border rounded-full text-[11px] uppercase tracking-widest transition-colors ${invoiceFilter === o.v ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}
                        data-testid={`invoice-filter-${o.v}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    {totalEwt > 0 && (
                      <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                        Total EWT Withheld · <span className="font-heading font-bold text-rose-700 normal-case tabular-nums" data-testid="invoice-ewt-total">{fmt(totalEwt)}</span>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-border"
                      onClick={async () => {
                        try {
                          const paidOnly = invoiceFilter === "paid";
                          await downloadFile(
                            `/exports/client/${c.id}/statement.pdf?paid_only=${paidOnly}`,
                            `${(c.name || "client").replace(/\s+/g, "_")}_statement.pdf`,
                          );
                          toast.success("Statement downloaded");
                        } catch { toast.error("Download failed"); }
                      }}
                      data-testid="print-statement-btn"
                    >
                      <Printer size={14} className="mr-1.5" /> Print Statement
                    </Button>
                  </div>
                </div>
                <div className="border border-border rounded-md bg-card overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Invoice No.</TableHead>
                      <TableHead>Issued</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">VAT</TableHead>
                      <TableHead className="text-right">EWT</TableHead>
                      <TableHead className="text-right">Collected</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Paid On</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {filtered.length === 0 ? (
                        <TableRow><TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">
                          {invoices.length === 0 ? "No invoices for this client yet." : `No invoices match “${invoiceFilter}”.`}
                        </TableCell></TableRow>
                      ) : filtered.map((inv) => {
                        const paymentsList = inv.payments || [];
                        const ewt = paymentsList.reduce((s, p) => s + (Number(p.ewt_amount) || 0), 0);
                        const lastPaid = paymentsList.length ? paymentsList[paymentsList.length - 1].date : "";
                        return (
                          <TableRow key={inv.id} data-testid={`linked-invoice-${inv.id}`}>
                            <TableCell className="font-mono text-xs">{inv.invoice_no}</TableCell>
                            <TableCell className="text-sm">{inv.issue_date || (inv.created_at || "").slice(0, 10)}</TableCell>
                            <TableCell className="text-right tabular-nums">{fmt(inv.total_amount)}</TableCell>
                            <TableCell className="text-right tabular-nums text-amber-700">{fmt(inv.vat_amount)}</TableCell>
                            <TableCell className="text-right tabular-nums text-rose-700">{ewt ? fmt(ewt) : "—"}</TableCell>
                            <TableCell className="text-right tabular-nums text-emerald-700">{fmt(inv.amount_paid)}</TableCell>
                            <TableCell className="text-right tabular-nums">{fmt(inv.balance)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{lastPaid ? String(lastPaid).slice(0, 10) : "—"}</TableCell>
                            <TableCell><StatusBadge status={inv.payment_status} /></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            );
          })()}
        </TabsContent>

        <TabsContent value="quotations" className="mt-4">
          <div className="border border-border rounded-md bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>No.</TableHead><TableHead>Subject</TableHead><TableHead>Supplier</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.quotations.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No quotations linked to this client yet.</TableCell></TableRow>
                ) : data.quotations.map((q) => (
                  <TableRow key={q.id} data-testid={`linked-quotation-${q.id}`}>
                    <TableCell className="font-mono text-xs">{q.quotation_no}</TableCell>
                    <TableCell className="font-medium">{q.subject}</TableCell>
                    <TableCell>{q.supplier_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(q.total_amount)}</TableCell>
                    <TableCell><StatusBadge status={q.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="pos" className="mt-4">
          <div className="border border-border rounded-md bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>PO Number</TableHead><TableHead>Supplier</TableHead><TableHead>Delivery</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.purchase_orders.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No purchase orders linked to this client yet.</TableCell></TableRow>
                ) : data.purchase_orders.map((p) => (
                  <TableRow key={p.id} data-testid={`linked-po-${p.id}`}>
                    <TableCell className="font-mono text-xs">{p.po_number}</TableCell>
                    <TableCell>{p.supplier_name}</TableCell>
                    <TableCell className="text-sm">{p.delivery_date || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(p.total_amount)}</TableCell>
                    <TableCell><StatusBadge status={p.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="billings" className="mt-4">
          <div className="border border-border rounded-md bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Billing No.</TableHead><TableHead>PO</TableHead><TableHead>Invoice</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Payment</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.billings.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No billing requests linked yet.</TableCell></TableRow>
                ) : data.billings.map((b) => (
                  <TableRow key={b.id} data-testid={`linked-billing-${b.id}`}>
                    <TableCell className="font-mono text-xs">{b.billing_no}</TableCell>
                    <TableCell className="font-mono text-xs">{b.po_number || "—"}</TableCell>
                    <TableCell className="text-sm">{b.invoice_number || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(b.amount)}</TableCell>
                    <TableCell><StatusBadge status={b.payment_status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {seesReports && (
          <TabsContent value="reports" className="mt-4">
            <div className="border border-border rounded-md bg-card overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Uploaded By</TableHead>
                  <TableHead>Uploaded</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {reports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                        No reports uploaded for this client yet. <Link to="/client-reports" className="underline">Upload one</Link>.
                      </TableCell>
                    </TableRow>
                  ) : reports.map((r) => {
                    const [y, m] = (r.report_month || "").split("-");
                    const monthLabel = y && m ? new Date(Number(y), Number(m) - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" }) : r.report_month;
                    const href = r.file_url?.startsWith("http") ? r.file_url : `${process.env.REACT_APP_BACKEND_URL || ""}${r.file_url}`;
                    return (
                      <TableRow key={r.id} data-testid={`linked-report-${r.id}`}>
                        <TableCell className="font-medium tabular-nums">{monthLabel}</TableCell>
                        <TableCell>
                          <div>{r.title}</div>
                          {r.notes && <div className="text-xs text-muted-foreground mt-0.5 max-w-md truncate" title={r.notes}>{r.notes}</div>}
                        </TableCell>
                        <TableCell>
                          <a href={href} target="_blank" rel="noreferrer" className="text-sm hover:underline flex items-center gap-1.5" data-testid={`download-linked-report-${r.id}`}>
                            <DownloadSimple size={14} /> {r.file_name || "download"}
                          </a>
                        </TableCell>
                        <TableCell className="text-sm">{r.uploaded_by_name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{(r.created_at || "").slice(0, 10)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </motion.div>
  );
}
